import React, { useState } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { Outlet, useNavigate } from 'react-router-dom';
import { useSession } from '../auth/SessionContext';
import { useI18n } from '../i18n';
import LanguageSwitcher from './LanguageSwitcher';

const LOGO_URL =
  'https://digestmed.ro/wp-content/uploads/2026/01/cropped-cropped-cropped-Logo-DigestMed.png';

const initialsOf = (fullName) =>
  (fullName || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

export default function AppLayout() {
  const navigate = useNavigate();
  const { user, logout } = useSession();
  const { t } = useI18n();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      <AppBar position="static" color="transparent" elevation={0}>
        <Toolbar sx={{ py: 1.5, gap: { xs: 1, sm: 0 } }}>
          <Box
            component="img"
            src={LOGO_URL}
            alt={t('app.logoAlt')}
            sx={{ height: { xs: 32, sm: 40 }, width: 'auto', mr: { xs: 0, sm: 2 } }}
          />
          <Typography
            variant="h6"
            color="text.primary"
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, lineHeight: 1.2 }}
          >
            {t('app.title')}
          </Typography>
          <Box
            sx={{
              ml: 'auto',
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              flexShrink: 0,
            }}
          >
            <LanguageSwitcher />
            {user && (
              <>
                <Button
                  color="inherit"
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                  aria-label={t('header.userMenu')}
                  aria-haspopup="true"
                  startIcon={
                    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                      {initialsOf(user.fullName)}
                    </Avatar>
                  }
                  sx={{
                    textTransform: 'none',
                    color: 'text.primary',
                    minWidth: 0,
                    '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 }, ml: 0 },
                  }}
                >
                  <Box
                    component="span"
                    sx={{ display: { xs: 'none', sm: 'inline' } }}
                  >
                    {user.fullName}
                  </Box>
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="body2" color="text.primary">
                      {user.fullName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                  <Divider />
                  <MenuItem onClick={handleLogout}>
                    <ListItemIcon>
                      <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    {t('header.logout')}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <Container maxWidth="xl">
        <Outlet />
      </Container>
    </Box>
  );
}
